import './StreamingMediaPlanner.scss';
import { addDays, format, isMonday, parseISO } from 'date-fns';
import {
  ChangeEvent,
  Fragment,
  KeyboardEvent as ReactKeyboardEvent,
  ReactElement,
  useEffect,
  useMemo,
  useState
} from 'react';
import { Form, InputGroup } from 'react-bootstrap';
import {
  RiAddLine,
  RiDownloadLine,
  RiFileCopyLine,
  RiFileExcel2Line,
  RiSaveLine,
  RiSubtractLine
} from 'react-icons/ri';
import { Button } from '@Components/Button';
import { DatePicker } from '@Components/DatePicker/DatePicker';
import { DateRange } from '@Components/DatePicker/date-picker-types';
import { Dropdown } from '@Components/Dropdown';
import { Appearance, Placement, Size } from '@Components/designConstants';
import { getFormattedValue } from '@Components/NumberFormatter/NumberFormatter';
import { Page } from '@Components/Page';
import { Spinner } from '@Components/Spinner/Spinner';
import { PAGE_NAMES } from '@Components/Page/page-constants';
import { AutocompleteCell } from './AutocompleteCell';
import { buildCellId, focusCellByDelta } from './grid-focus';
import { useAppSelector } from '../../redux';
import { currentCompanySelector } from '../../redux/company';
import { emailSelector } from '../../redux/user';
import { downloadJSONToCSV } from '../../utils/download-utils';
import { awaitJSON, StreamingV2LambdaFetch } from '../../utils/fetch-utils';
import {
  aggregatePlans,
  BuyableStreamingMediaRow,
  buildMediaCatalog,
  computeImpressions,
  computeWeekSummary,
  DIMENSIONS,
  FIXED_COLUMNS,
  FixedValues,
  formatImpressions,
  getColumnOptions,
  loadSavedPlans,
  loadSavedSummaries,
  MediaCatalog,
  Metric,
  MetricKey,
  METRICS,
  NetworkProfile,
  PlanType,
  PlanTypeValue,
  Row,
  SavedPlan,
  SavedSummary,
  Week,
  deleteSavedPlan as deleteSavedPlanStorage,
  deleteSavedSummary as deleteSavedSummaryStorage,
  snapToMondaySundayWeek,
  upsertSavedPlan,
  upsertSavedSummary
} from './streaming-media-planner-utils';
import { SavedPlansTab } from './SavedPlansTab';
import { SavedSummariesTab } from './SavedSummariesTab';
import { SavePlanModal } from './SavePlanModal';
import { TableViewTab } from './TableViewTab';
import { Mixpanel, MxE } from '../../utils/mixpanelWrapper';

const PLAN_ENTITY = 'plan';
const SUMMARY_ENTITY = 'summary';

const PLAN_TYPE_OPTIONS = [
  { label: 'Streaming & Display', value: PlanType.STREAMING_DISPLAY },
  { label: 'Audio & Podcast', value: PlanType.AUDIO_PODCAST }
];

const emptyFixed = (): FixedValues =>
  Object.fromEntries(FIXED_COLUMNS.map(col => [col.key, undefined]));

const newRow = (weekCount: number): Row => ({
  fixed: emptyFixed(),
  weekData: Array.from({ length: weekCount }, () => ({}))
});

const formatDollar = (n: number, decimals = 0): string => {
  if (!Number.isFinite(n) || n <= 0) return '—';
  return getFormattedValue({ value: n, type: '$', decimals });
};

const formatInteger = (n: number): string => {
  if (!Number.isFinite(n) || n <= 0) return '—';
  return getFormattedValue({ value: n, type: '#0' });
};

const rowHasAnyValue = (row: Row): boolean =>
  FIXED_COLUMNS.some(col => Boolean(row.fixed[col.key])) ||
  row.weekData.some(wd => Boolean(wd.cpm) || Boolean(wd.investment));

// Column count helpers — includes the row-remove column (the +1 / +11 magic numbers).
const REMOVE_COL_COUNT = 1;
const LABEL_COLSPAN = REMOVE_COL_COUNT + FIXED_COLUMNS.length; // 11
const WIDE_FIXED_COL_COUNT = 5; // first 5 fixed cols get wider rendering
const WEEK_COL_COUNT = 3; // CPM + Investment + Impressions per week

const Tab = {
  PLAN: 'plan',
  SAVED: 'saved',
  TABLE_VIEW: 'table-view',
  SAVED_SUMMARIES: 'saved-summaries'
} as const;
type TabValue = (typeof Tab)[keyof typeof Tab];

const TAB_NAVS = [
  { key: Tab.PLAN, label: 'Plan Builder' },
  { key: Tab.SAVED, label: 'My Saved Plans' },
  { key: Tab.TABLE_VIEW, label: 'Summaries' },
  { key: Tab.SAVED_SUMMARIES, label: 'My Saved Summaries' }
];

export const StreamingMediaPlanner = (): ReactElement => {
  const [planType, setPlanType] = useState<PlanTypeValue>(PlanType.STREAMING_DISPLAY);
  const [weeks, setWeeks] = useState<Week[]>([{ startDate: '', endDate: '' }]);
  const [rows, setRows] = useState<Row[]>([newRow(1)]);
  const [activeTab, setActiveTab] = useState<TabValue>(Tab.PLAN);
  const [savedPlans, setSavedPlans] = useState<SavedPlan[]>(() => loadSavedPlans());
  const [currentPlanId, setCurrentPlanId] = useState<number | null>(null);
  const [currentPlanName, setCurrentPlanName] = useState<string>('');
  const [showSaveModal, setShowSaveModal] = useState(false);

  const [savedSummaries, setSavedSummaries] = useState<SavedSummary[]>(() => loadSavedSummaries());
  const [currentSummaryId, setCurrentSummaryId] = useState<number | null>(null);
  const [currentSummaryName, setCurrentSummaryName] = useState<string>('');
  const [summaryRange, setSummaryRange] = useState<DateRange>({ start: '', end: '' });
  const [summaryDimensions, setSummaryDimensions] = useState<string[]>(['network']);
  const [summaryMetrics, setSummaryMetrics] = useState<MetricKey[]>([
    Metric.INVESTMENT,
    Metric.IMPRESSIONS
  ]);
  const [showSaveSummaryModal, setShowSaveSummaryModal] = useState(false);

  const userEmail = useAppSelector(emailSelector);
  const company = useAppSelector(currentCompanySelector);

  // Store the raw API rows; the catalog is derived per planType so flipping
  // between Streaming/Display and Audio/Podcast doesn't require a refetch.
  const [mediaRows, setMediaRows] = useState<readonly BuyableStreamingMediaRow[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  useEffect(() => {
    if (!company) {
      setMediaRows([]);
      setCatalogLoading(false);
      return undefined;
    }
    setCatalogLoading(true);
    let bail = false;
    (async () => {
      try {
        const res = await StreamingV2LambdaFetch('/buyable_streaming_media', {
          params: { company }
        });
        const fetched = await awaitJSON<BuyableStreamingMediaRow[]>(res);
        if (bail) return;
        setMediaRows(fetched);
      } catch (e) {
        if (bail) return;
        console.error('Failed to load buyable_streaming_media catalog', e);
        setMediaRows([]);
      } finally {
        if (!bail) {
          setCatalogLoading(false);
        }
      }
    })();
    return () => {
      bail = true;
    };
  }, [company]);

  const catalog = useMemo<MediaCatalog>(
    () => buildMediaCatalog(mediaRows, planType),
    [mediaRows, planType]
  );

  useEffect(() => {
    if (currentPlanId === null) return;
    if (!savedPlans.some(p => p.id === currentPlanId)) {
      setCurrentPlanId(null);
      setCurrentPlanName('');
    }
  }, [savedPlans, currentPlanId]);

  useEffect(() => {
    if (currentSummaryId === null) return;
    if (!savedSummaries.some(s => s.id === currentSummaryId)) {
      setCurrentSummaryId(null);
      setCurrentSummaryName('');
    }
  }, [savedSummaries, currentSummaryId]);

  const aggregatedSummary = useMemo(
    () => aggregatePlans(savedPlans, summaryRange.start, summaryRange.end, summaryDimensions),
    [savedPlans, summaryRange.start, summaryRange.end, summaryDimensions]
  );

  const handleFixedChange = (rowIndex: number, colKey: string, value: string): void => {
    setRows(prev =>
      prev.map((row, i) => {
        if (i !== rowIndex) return row;
        const nextFixed: FixedValues = { ...row.fixed, [colKey]: value };
        // When Network changes, clear downstream values that aren't valid for the new network,
        // then auto-select any field that has exactly one available option.
        if (colKey === 'network') {
          const profile = catalog.profiles[value];
          if (profile) {
            for (const k of Object.keys(profile) as (keyof NetworkProfile)[]) {
              const allowed = profile[k];
              const current = nextFixed[k];
              if (current && allowed.length > 0 && !allowed.includes(current)) {
                nextFixed[k] = undefined;
              }
            }
            for (const k of Object.keys(profile) as (keyof NetworkProfile)[]) {
              if (!nextFixed[k] && profile[k].length === 1) {
                const [singleOption] = profile[k];
                nextFixed[k] = singleOption;
              }
            }
          }
        }
        // DSP is only valid for RTB platform buys.
        if (nextFixed.platform !== 'RTB') {
          nextFixed.dsp = undefined;
        }
        return { ...row, fixed: nextFixed };
      })
    );
  };

  const handleGridInputKey = (e: ReactKeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      focusCellByDelta(e.currentTarget, 'up');
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      focusCellByDelta(e.currentTarget, 'down');
    } else if (e.key === 'Enter') {
      e.preventDefault();
      focusCellByDelta(e.currentTarget, 'next');
    }
  };

  const handleWeekDataChange = (
    rowIndex: number,
    weekIndex: number,
    field: 'cpm' | 'investment',
    value: string
  ): void => {
    setRows(prev =>
      prev.map((row, ri) => {
        if (ri !== rowIndex) return row;
        const nextWeekData = row.weekData.map((wd, wi) =>
          wi === weekIndex ? { ...wd, [field]: value } : wd
        );
        return { ...row, weekData: nextWeekData };
      })
    );
  };

  const handleDateRangeChange = (weekIndex: number, range: DateRange): void => {
    const snapped = snapToMondaySundayWeek(range.start || range.end);
    if (!snapped) return;
    setWeeks(prev =>
      prev.map((week, wi) =>
        wi === weekIndex ? { ...week, startDate: snapped.start, endDate: snapped.end } : week
      )
    );
  };

  const handleBpmChange = (
    weekIndex: number,
    field: 'adServingEstimate' | 'feeEstimate' | 'clearanceAssumption',
    value: string
  ): void => {
    setWeeks(prev =>
      prev.map((week, wi) => (wi === weekIndex ? { ...week, [field]: value } : week))
    );
  };

  const handleAddWeekAfter = (weekIndex: number): void => {
    setWeeks(prev => [
      ...prev.slice(0, weekIndex + 1),
      { startDate: '', endDate: '' },
      ...prev.slice(weekIndex + 1)
    ]);
    setRows(prev =>
      prev.map(row => ({
        ...row,
        weekData: [
          ...row.weekData.slice(0, weekIndex + 1),
          {},
          ...row.weekData.slice(weekIndex + 1)
        ]
      }))
    );
  };

  const handleAddRowAfter = (rowIndex: number): void => {
    setRows(prev => [
      ...prev.slice(0, rowIndex + 1),
      newRow(weeks.length),
      ...prev.slice(rowIndex + 1)
    ]);
  };

  const handleRemoveWeek = (weekIndex: number): void => {
    if (weeks.length <= 1) return;
    setWeeks(prev => prev.filter((_, wi) => wi !== weekIndex));
    setRows(prev =>
      prev.map(row => ({ ...row, weekData: row.weekData.filter((_, wi) => wi !== weekIndex) }))
    );
  };

  const handleRemoveRow = (rowIndex: number): void => {
    if (rows.length <= 1) return;
    setRows(prev => prev.filter((_, ri) => ri !== rowIndex));
  };

  const handleCloneRow = (rowIndex: number): void => {
    setRows(prev => {
      const source = prev[rowIndex];
      const clone: Row = {
        fixed: { ...source.fixed },
        weekData: source.weekData.map(wd => ({ ...wd }))
      };
      return [...prev.slice(0, rowIndex + 1), clone, ...prev.slice(rowIndex + 1)];
    });
  };

  const handleCloneWeek = (weekIndex: number): void => {
    // Auto-advance the cloned week by 7 days; preserve BPM estimates.
    const advanceIso = (iso: string | undefined): string => {
      if (!iso) return '';
      const parsed = parseISO(iso);
      if (Number.isNaN(parsed.getTime())) return '';
      return format(addDays(parsed, 7), 'yyyy-MM-dd');
    };
    setWeeks(prev => {
      const source = prev[weekIndex];
      const clone: Week = {
        startDate: advanceIso(source.startDate),
        endDate: advanceIso(source.endDate),
        adServingEstimate: source.adServingEstimate,
        feeEstimate: source.feeEstimate,
        clearanceAssumption: source.clearanceAssumption
      };
      return [...prev.slice(0, weekIndex + 1), clone, ...prev.slice(weekIndex + 1)];
    });
    setRows(prev =>
      prev.map(row => {
        const sourceWd = row.weekData[weekIndex] ?? {};
        const clonedWd = { ...sourceWd };
        return {
          ...row,
          weekData: [
            ...row.weekData.slice(0, weekIndex + 1),
            clonedWd,
            ...row.weekData.slice(weekIndex + 1)
          ]
        };
      })
    );
  };

  const weekSummaries = weeks.map((week, wi) => computeWeekSummary(rows, week, wi));

  const exportData: Record<string, string>[] = [];
  for (const row of rows) {
    if (!rowHasAnyValue(row)) continue;
    for (let wi = 0; wi < weeks.length; wi += 1) {
      const wd = row.weekData[wi] ?? {};
      const week = weeks[wi];
      const entry: Record<string, string> = {
        'Start Date': week.startDate,
        'End Date': week.endDate
      };
      for (const col of FIXED_COLUMNS) {
        entry[col.label] = row.fixed[col.key] ?? '';
      }
      entry.CPM = wd.cpm ?? '';
      entry.Investment = wd.investment ?? '';
      entry.Impressions = formatImpressions(computeImpressions(wd));
      exportData.push(entry);
    }
  }
  const handleExport = (): void => {
    downloadJSONToCSV(exportData, 'streaming-media-planner');
  };

  const handleSaveNewPlan = (name: string): void => {
    const { plans, saved } = upsertSavedPlan(savedPlans, {
      name,
      lastEditor: userEmail || 'Unknown',
      planType,
      weeks,
      rows
    });
    setSavedPlans(plans);
    Mixpanel.track(MxE.RecordSaved, {
      page: PAGE_NAMES.STREAMING_MEDIA_PLANNER,
      entity: PLAN_ENTITY
    });
    setCurrentPlanId(saved.id);
    setCurrentPlanName(saved.name);
    setShowSaveModal(false);
  };

  const handleSaveEditsToExisting = (): void => {
    if (currentPlanId === null) return;
    const { plans, saved } = upsertSavedPlan(savedPlans, {
      id: currentPlanId,
      name: currentPlanName,
      lastEditor: userEmail || 'Unknown',
      planType,
      weeks,
      rows
    });
    setSavedPlans(plans);
    Mixpanel.track(MxE.RecordSaved, {
      page: PAGE_NAMES.STREAMING_MEDIA_PLANNER,
      entity: PLAN_ENTITY
    });
    setCurrentPlanName(saved.name);
  };

  const handleOpenSavedPlan = (plan: SavedPlan): void => {
    setPlanType(plan.planType);
    setWeeks(plan.weeks);
    setRows(plan.rows);
    setCurrentPlanId(plan.id);
    setCurrentPlanName(plan.name);
    setActiveTab(Tab.PLAN);
  };

  const handleDeleteSavedPlan = (plan: SavedPlan): void => {
    const next = deleteSavedPlanStorage(savedPlans, plan.id);
    setSavedPlans(next);
  };

  const handleSaveNewSummary = (name: string): void => {
    const { summaries, saved } = upsertSavedSummary(savedSummaries, {
      name,
      lastEditor: userEmail || 'Unknown',
      rangeStart: summaryRange.start,
      rangeEnd: summaryRange.end,
      dimensions: summaryDimensions,
      metrics: summaryMetrics
    });
    setSavedSummaries(summaries);
    Mixpanel.track(MxE.RecordSaved, {
      page: PAGE_NAMES.STREAMING_MEDIA_PLANNER,
      entity: SUMMARY_ENTITY
    });
    setCurrentSummaryId(saved.id);
    setCurrentSummaryName(saved.name);
    setShowSaveSummaryModal(false);
  };

  const handleSaveEditsToExistingSummary = (): void => {
    if (currentSummaryId === null) return;
    const { summaries, saved } = upsertSavedSummary(savedSummaries, {
      id: currentSummaryId,
      name: currentSummaryName,
      lastEditor: userEmail || 'Unknown',
      rangeStart: summaryRange.start,
      rangeEnd: summaryRange.end,
      dimensions: summaryDimensions,
      metrics: summaryMetrics
    });
    setSavedSummaries(summaries);
    Mixpanel.track(MxE.RecordSaved, {
      page: PAGE_NAMES.STREAMING_MEDIA_PLANNER,
      entity: SUMMARY_ENTITY
    });
    setCurrentSummaryName(saved.name);
  };

  const handleOpenSavedSummary = (summary: SavedSummary): void => {
    setSummaryRange({ start: summary.rangeStart, end: summary.rangeEnd });
    setSummaryDimensions(summary.dimensions);
    setSummaryMetrics(summary.metrics);
    setCurrentSummaryId(summary.id);
    setCurrentSummaryName(summary.name);
    setActiveTab(Tab.TABLE_VIEW);
  };

  const handleDeleteSavedSummary = (summary: SavedSummary): void => {
    const next = deleteSavedSummaryStorage(savedSummaries, summary.id);
    setSavedSummaries(next);
  };

  const handleSummaryExportCsv = (): void => {
    const exportRows: Record<string, string>[] = aggregatedSummary.map(aggRow => {
      const entry: Record<string, string> = {};
      for (const dimKey of summaryDimensions) {
        const dim = DIMENSIONS.find(d => d.key === dimKey);
        entry[dim?.label ?? dimKey] = aggRow.dimensions[dimKey] ?? '';
      }
      for (const metricKey of summaryMetrics) {
        const metric = METRICS.find(m => m.key === metricKey);
        entry[metric?.label ?? metricKey] = String(aggRow.totals[metricKey] ?? 0);
      }
      return entry;
    });
    const fileName = currentSummaryName ? `summary-${currentSummaryName}` : 'summary';
    downloadJSONToCSV(exportRows, fileName);
  };

  const handleSummaryExportGSheets = (): void => {
    // eslint-disable-next-line no-alert
    window.alert('Google Sheets export coming soon.');
  };

  const isUpdate = currentPlanId !== null;
  const isSummaryUpdate = currentSummaryId !== null;
  const hasSummaryRange = Boolean(summaryRange.start) && Boolean(summaryRange.end);
  const hasSummaryData = aggregatedSummary.length > 0;

  const pageTitle = ((): string => {
    if (activeTab === Tab.PLAN && currentPlanName) {
      return `Streaming Plans Builder — ${currentPlanName}`;
    }
    if (activeTab === Tab.TABLE_VIEW && currentSummaryName) {
      return `Streaming Plans Builder — ${currentSummaryName}`;
    }
    return 'Streaming Plans Builder';
  })();

  return (
    <Page
      title={pageTitle}
      pageType={PAGE_NAMES.STREAMING_MEDIA_PLANNER}
      navs={TAB_NAVS}
      selectedNav={activeTab}
      onNav={key => {
        setActiveTab(key as TabValue);
        Mixpanel.track(MxE.TabChange, {
          page: PAGE_NAMES.STREAMING_MEDIA_PLANNER,
          option_selected: key
        });
      }}
      actions={
        <>
          {activeTab === Tab.PLAN && (
            <div className='streaming-media-planner-header-actions'>
              {isUpdate && (
                <Button
                  size={Size.LG}
                  onClick={handleSaveEditsToExisting}
                  leadingElement={<RiSaveLine />}
                >
                  Save Edits to Existing Plan
                </Button>
              )}
              <Button
                size={Size.LG}
                onClick={() => {
                  setShowSaveModal(true);
                  Mixpanel.track(MxE.ModalOpened, {
                    page: PAGE_NAMES.STREAMING_MEDIA_PLANNER,
                    modal: 'save_plan'
                  });
                }}
                leadingElement={<RiSaveLine />}
              >
                Save New Plan
              </Button>
              <Button size={Size.LG} onClick={handleExport} leadingElement={<RiDownloadLine />}>
                Export CSV
              </Button>
            </div>
          )}
          {activeTab === Tab.TABLE_VIEW && (
            <div className='streaming-media-planner-header-actions'>
              {isSummaryUpdate && (
                <Button
                  size={Size.LG}
                  onClick={handleSaveEditsToExistingSummary}
                  leadingElement={<RiSaveLine />}
                >
                  Save Edits to Existing Summary
                </Button>
              )}
              <Button
                size={Size.LG}
                onClick={() => {
                  setShowSaveSummaryModal(true);
                  Mixpanel.track(MxE.ModalOpened, {
                    page: PAGE_NAMES.STREAMING_MEDIA_PLANNER,
                    modal: 'save_summary'
                  });
                }}
                disabled={!hasSummaryRange}
                leadingElement={<RiSaveLine />}
              >
                Save New Summary
              </Button>
              <Button
                size={Size.LG}
                onClick={handleSummaryExportCsv}
                disabled={!hasSummaryData}
                leadingElement={<RiDownloadLine />}
              >
                Export CSV
              </Button>
              <Button
                size={Size.LG}
                onClick={handleSummaryExportGSheets}
                disabled={!hasSummaryData}
                leadingElement={<RiFileExcel2Line />}
              >
                Export to Google Sheets
              </Button>
            </div>
          )}
        </>
      }
    >
      <div className='streaming-media-planner-page'>
        {activeTab === Tab.SAVED && (
          <SavedPlansTab
            plans={savedPlans}
            onOpen={handleOpenSavedPlan}
            onDelete={handleDeleteSavedPlan}
          />
        )}
        {activeTab === Tab.TABLE_VIEW && (
          <TableViewTab
            range={summaryRange}
            onRangeChange={setSummaryRange}
            selectedDimensions={summaryDimensions}
            onDimensionsChange={setSummaryDimensions}
            selectedMetrics={summaryMetrics}
            onMetricsChange={setSummaryMetrics}
            aggregated={aggregatedSummary}
          />
        )}
        {activeTab === Tab.SAVED_SUMMARIES && (
          <SavedSummariesTab
            summaries={savedSummaries}
            onOpen={handleOpenSavedSummary}
            onDelete={handleDeleteSavedSummary}
          />
        )}
        {activeTab === Tab.PLAN && renderPlanEditor()}

        {showSaveModal && (
          <SavePlanModal onCancel={() => setShowSaveModal(false)} onSave={handleSaveNewPlan} />
        )}
        {showSaveSummaryModal && (
          <SavePlanModal
            entityLabel='Summary'
            onCancel={() => setShowSaveSummaryModal(false)}
            onSave={handleSaveNewSummary}
          />
        )}
      </div>
    </Page>
  );

  function renderPlanEditor(): ReactElement {
    let planBody: ReactElement;
    if (catalogLoading) {
      planBody = (
        <div className='catalog-loading'>
          <Spinner />
        </div>
      );
    } else if (planType === PlanType.AUDIO_PODCAST) {
      planBody = (
        <div className='plan-placeholder'>
          Audio &amp; Podcast plan coming soon — will expand on the Streaming &amp; Display
          structure.
        </div>
      );
    } else {
      planBody = (
        <table className='streaming-media-planner-table'>
          <colgroup>
            <col className='col-remove' />
            {FIXED_COLUMNS.map((col, ci) => (
              <col
                key={col.key}
                className={ci < WIDE_FIXED_COL_COUNT ? 'col-frozen-wide' : 'col-frozen-narrow'}
              />
            ))}
            {weeks.flatMap((_, wi) => [
              <col key={`${wi}-cpm`} className='col-week' />,
              <col key={`${wi}-inv`} className='col-week' />,
              <col key={`${wi}-impr`} className='col-week' />
            ])}
          </colgroup>
          <thead>
            <tr className='week-controls-row'>
              <td colSpan={LABEL_COLSPAN} className='week-controls-spacer'>
                {' '}
              </td>
              {weeks.map((_, wi) => (
                <td key={wi} colSpan={WEEK_COL_COUNT} className='week-remove-cell'>
                  <div className='cell-actions'>
                    <Button
                      appearance={Appearance.EMPTY}
                      size={Size.SM}
                      onClick={() => handleAddWeekAfter(wi)}
                      tooltip={{ text: 'Add investment column' }}
                    >
                      <RiAddLine />
                    </Button>
                    <Button
                      appearance={Appearance.EMPTY}
                      size={Size.SM}
                      onClick={() => handleCloneWeek(wi)}
                      tooltip={{ text: 'Clone this investment column (next week)' }}
                    >
                      <RiFileCopyLine />
                    </Button>
                    <Button
                      appearance={Appearance.EMPTY}
                      size={Size.SM}
                      onClick={() => handleRemoveWeek(wi)}
                      disabled={weeks.length <= 1}
                      tooltip={{ text: 'Remove this investment column' }}
                    >
                      <RiSubtractLine />
                    </Button>
                  </div>
                </td>
              ))}
            </tr>
            <tr className='week-row'>
              <td colSpan={LABEL_COLSPAN} className='frozen frozen-0 week-row-spacer'>
                {' '}
              </td>
              {weeks.map((week, wi) => (
                <th key={wi} colSpan={WEEK_COL_COUNT} className='week-header'>
                  <div className='week-title'>Week {wi + 1}</div>
                  <div className='week-dates'>
                    <DatePicker
                      range={{ start: week.startDate, end: week.endDate }}
                      onChange={range => handleDateRangeChange(wi, range)}
                      isOutsideRange={() => false}
                      isDayBlocked={date => !isMonday(parseISO(date))}
                      allowOnlyStartDates
                      size={Size.SM}
                    />
                  </div>
                </th>
              ))}
            </tr>
            <tr>
              <th className='frozen frozen-0 remove-col-header' aria-hidden='true'>
                {' '}
              </th>
              {FIXED_COLUMNS.map((col, ci) => (
                <th key={col.key} className={`frozen frozen-${ci + 1}`}>
                  {col.label}
                </th>
              ))}
              {weeks.map((_, wi) => (
                <Fragment key={wi}>
                  <th>CPM</th>
                  <th>Investment</th>
                  <th>Impressions</th>
                </Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri}>
                <td className='frozen frozen-0 row-remove-cell'>
                  <div className='cell-actions'>
                    <Button
                      appearance={Appearance.EMPTY}
                      size={Size.SM}
                      onClick={() => handleAddRowAfter(ri)}
                      tooltip={{ text: 'Add row' }}
                    >
                      <RiAddLine />
                    </Button>
                    <Button
                      appearance={Appearance.EMPTY}
                      size={Size.SM}
                      onClick={() => handleCloneRow(ri)}
                      tooltip={{ text: 'Clone this row' }}
                    >
                      <RiFileCopyLine />
                    </Button>
                    <Button
                      appearance={Appearance.EMPTY}
                      size={Size.SM}
                      onClick={() => handleRemoveRow(ri)}
                      disabled={rows.length <= 1}
                      tooltip={{ text: 'Remove this row' }}
                    >
                      <RiSubtractLine />
                    </Button>
                  </div>
                </td>
                {FIXED_COLUMNS.map((col, ci) => {
                  const options = getColumnOptions(col.key, row.fixed.network, catalog);
                  return (
                    <td key={col.key} className={`frozen frozen-${ci + 1} editable-cell`}>
                      <AutocompleteCell
                        value={row.fixed[col.key]}
                        options={options}
                        onCommit={value => handleFixedChange(ri, col.key, value)}
                        cellId={buildCellId.fixed(ri, col.key)}
                      />
                    </td>
                  );
                })}
                {row.weekData.map((wd, wi) => (
                  <Fragment key={wi}>
                    <td className='editable-cell'>
                      <InputGroup size='sm'>
                        <InputGroup.Text>$</InputGroup.Text>
                        <Form.Control
                          type='text'
                          value={wd.cpm ?? ''}
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            handleWeekDataChange(ri, wi, 'cpm', e.target.value)
                          }
                          onKeyDown={handleGridInputKey}
                          data-grid-cell={buildCellId.week(ri, wi, 'cpm')}
                          placeholder='0'
                        />
                      </InputGroup>
                    </td>
                    <td className='editable-cell'>
                      <InputGroup size='sm'>
                        <InputGroup.Text>$</InputGroup.Text>
                        <Form.Control
                          type='text'
                          value={wd.investment ?? ''}
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            handleWeekDataChange(ri, wi, 'investment', e.target.value)
                          }
                          onKeyDown={handleGridInputKey}
                          data-grid-cell={buildCellId.week(ri, wi, 'investment')}
                          placeholder='0'
                        />
                      </InputGroup>
                    </td>
                    <td className='calculated-cell'>{formatImpressions(computeImpressions(wd))}</td>
                  </Fragment>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className='totals-row'>
              <td colSpan={LABEL_COLSPAN} className='frozen frozen-0 total-label'>
                Total
              </td>
              {weekSummaries.map((summary, wi) => (
                <Fragment key={wi}>
                  <td className='total-num'>{formatDollar(summary.avgCpm, 2)}</td>
                  <td className='total-num'>{formatDollar(summary.totalInvestment)}</td>
                  <td className='total-num'>{formatInteger(summary.totalImpressions)}</td>
                </Fragment>
              ))}
            </tr>
            <tr className='totals-row bpm-input-row'>
              <td colSpan={LABEL_COLSPAN} className='frozen frozen-0 total-label'>
                BPM Ad Serving Estimate
              </td>
              {weeks.map((week, wi) => (
                <td key={wi} colSpan={WEEK_COL_COUNT} className='bpm-input-cell'>
                  <InputGroup size='sm'>
                    <Form.Control
                      type='text'
                      value={week.adServingEstimate ?? ''}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        handleBpmChange(wi, 'adServingEstimate', e.target.value)
                      }
                      onKeyDown={handleGridInputKey}
                      data-grid-cell={buildCellId.bpm('ad', wi)}
                      placeholder='0'
                    />
                    <InputGroup.Text>%</InputGroup.Text>
                  </InputGroup>
                </td>
              ))}
            </tr>
            <tr className='totals-row bpm-input-row'>
              <td colSpan={LABEL_COLSPAN} className='frozen frozen-0 total-label'>
                BPM Fee Estimate
              </td>
              {weeks.map((week, wi) => (
                <td key={wi} colSpan={WEEK_COL_COUNT} className='bpm-input-cell'>
                  <InputGroup size='sm'>
                    <Form.Control
                      type='text'
                      value={week.feeEstimate ?? ''}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        handleBpmChange(wi, 'feeEstimate', e.target.value)
                      }
                      onKeyDown={handleGridInputKey}
                      data-grid-cell={buildCellId.bpm('fee', wi)}
                      placeholder='0'
                    />
                    <InputGroup.Text>%</InputGroup.Text>
                  </InputGroup>
                </td>
              ))}
            </tr>
            <tr className='totals-row'>
              <td colSpan={LABEL_COLSPAN} className='frozen frozen-0 total-label'>
                BPM Ad Serving
              </td>
              {weekSummaries.map((summary, wi) => (
                <td key={wi} colSpan={WEEK_COL_COUNT} className='total-num'>
                  {formatDollar(summary.bpmAdServing, 2)}
                </td>
              ))}
            </tr>
            <tr className='totals-row'>
              <td colSpan={LABEL_COLSPAN} className='frozen frozen-0 total-label'>
                BPM Fee
              </td>
              {weekSummaries.map((summary, wi) => (
                <td key={wi} colSpan={WEEK_COL_COUNT} className='total-num'>
                  {formatDollar(summary.bpmFee, 2)}
                </td>
              ))}
            </tr>
            <tr className='totals-row grand-total-row'>
              <td colSpan={LABEL_COLSPAN} className='frozen frozen-0 total-label'>
                Grand Total
              </td>
              {weekSummaries.map((summary, wi) => (
                <td key={wi} colSpan={WEEK_COL_COUNT} className='total-num'>
                  {formatDollar(summary.grandTotal, 2)}
                </td>
              ))}
            </tr>
            <tr className='totals-row bpm-input-row'>
              <td colSpan={LABEL_COLSPAN} className='frozen frozen-0 total-label'>
                Clearance Assumption
              </td>
              {weeks.map((week, wi) => (
                <td key={wi} colSpan={WEEK_COL_COUNT} className='bpm-input-cell'>
                  <InputGroup size='sm'>
                    <Form.Control
                      type='text'
                      value={week.clearanceAssumption ?? ''}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        handleBpmChange(wi, 'clearanceAssumption', e.target.value)
                      }
                      onKeyDown={handleGridInputKey}
                      data-grid-cell={buildCellId.bpm('cl', wi)}
                      placeholder='0'
                    />
                    <InputGroup.Text>%</InputGroup.Text>
                  </InputGroup>
                </td>
              ))}
            </tr>
            <tr className='totals-row'>
              <td colSpan={LABEL_COLSPAN} className='frozen frozen-0 total-label'>
                Estimated Spend
              </td>
              {weekSummaries.map((summary, wi) => (
                <td key={wi} colSpan={WEEK_COL_COUNT} className='total-num'>
                  {formatDollar(summary.estimatedSpend, 2)}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      );
    }

    return (
      <>
        <div className='plan-type-section'>
          <span className='plan-type-label'>Plan Type</span>
          <Dropdown
            overlayPlacement={Placement.RIGHT}
            options={PLAN_TYPE_OPTIONS}
            value={planType}
            onChange={value => setPlanType(value)}
            size={Size.SM}
          />
        </div>
        {planBody}
      </>
    );
  }
};
